import React, { useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { Breadcrumb, Template, Status } from '@components'
import CardStatus from '@components/CardStatus'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { clientApi } from '@services/ClientApi'
import { formatCpfCnpj } from '@utils/helpers/CpfCnpjFormated'
import { createBreadcrumbLink, useCreateBreadcrumb } from '@utils/hooks'

const styles = {
	container: 'mt-8',
	header: 'mb-6',
	title: 'text-2xl font-bold text-gray-800',
	subtitle: 'text-gray-500',
	sectionTitle: 'text-lg font-semibold text-gray-700 mb-4',
	cardContainer: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8',
	infoGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-8',
	infoItem: 'flex flex-col',
	infoLabel: 'text-sm text-gray-500',
	infoValue: 'font-medium text-gray-800',
}

export default function SuspiciousDownloadDetails() {
	const { id } = useParams<{ id: string }>()
	const [searchParams, setSearchParams] = useSearchParams()
	const page = Number(searchParams.get('page')) || 1
	const limit = Number(searchParams.get('limit')) || 20

	const { data, isLoading } = clientApi.useGetSuspiciousDownloadDetailsQuery(
		{ clientId: id || '', page, limit },
		{ skip: !id }
	)

	const allPages = useMemo(() => {
		return [
			createBreadcrumbLink('Clientes', '/clientes', 1),
			createBreadcrumbLink('Downloads', '/clientes/downloads', 2),
			createBreadcrumbLink(
				data?.user?.name || 'Detalhes',
				`/clientes/downloads/${id}`,
				3
			),
		]
	}, [data?.user?.name, id])

	const pages = useCreateBreadcrumb(allPages)

	if (isLoading) {
		return (
			<Template>
				<div className='flex justify-center mt-10'>
					<Spinner size='md' />
				</div>
			</Template>
		)
	}

	if (!data) {
		return (
			<Template>
				<div>Dados não encontrados</div>
			</Template>
		)
	}

	const { user, stats, files } = data
	const paginatedFiles = files?.data || []
	const filesMeta = files?.meta

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<div className={styles.container}>
				<div className={styles.header}>
					<h1 className={styles.title}>Detalhes de Downloads</h1>
					<p className={styles.subtitle}>Análise detalhada para {user.name}</p>
				</div>

				<div className={styles.infoGrid}>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Nome</span>
						<span className={styles.infoValue}>{user.name}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Email</span>
						<span className={styles.infoValue}>{user.email}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>CPF/CNPJ</span>
						<span className={styles.infoValue}>
							{user.cpfCnpj ? formatCpfCnpj(user.cpfCnpj) : '-'}
						</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Status Geral</span>
						<span className={styles.infoValue}>
							<Status
								status={user.exceededWeeklyDownloadLimit ? 'danger' : 'success'}
							>
								{user.exceededWeeklyDownloadLimit ? 'Suspeito' : 'Normal'}
							</Status>
						</span>
					</div>
				</div>

				<Divider />

				<h2 className={styles.sectionTitle}>
					Estatísticas dos Últimos {stats.windowDays} Dias
				</h2>

				<div className={styles.cardContainer}>
					<CardStatus
						title='Total na Semana'
						value={stats.weekTotal}
						icon='file-icon'
					/>
					<CardStatus
						title='Máximo em um Dia'
						value={stats.dayMax}
						icon='file-icon'
					/>
					<CardStatus
						title='Downloads Hoje'
						value={stats.todayCount}
						icon='file-icon'
					/>
				</div>

				<div className='flex items-center justify-between mb-4'>
					<h2 className={styles.sectionTitle}>
						Arquivos Baixados (últimos 7 dias)
					</h2>
					<Link
						to={`/clientes/downloads/${id}/historico`}
						className='text-blue-600 hover:underline text-sm font-medium'
					>
						Ver histórico completo →
					</Link>
				</div>
				<Table
					th={[
						{ label: 'Nome do Arquivo' },
						{ label: 'Data' },
						{ label: 'Módulo' },
						{ label: 'IP' },
					]}
				>
					{paginatedFiles.map((file) => (
						<Tr key={file.id}>
							<Td>{file.file?.name || '-'}</Td>
							<Td>{file.downloadedAt || '-'}</Td>
							<Td>{file.moduleName || '-'}</Td>
							<Td>{file.ip || '-'}</Td>
						</Tr>
					))}
					{paginatedFiles.length === 0 && (
						<EmptyRow
							message='Nenhum arquivo baixado nos últimos 7 dias'
							tdAmount={4}
						/>
					)}
				</Table>

				{/* Paginação dos arquivos */}
				{filesMeta && filesMeta.lastPage > 1 && (
					<div className='flex justify-center mt-4'>
						<nav className='inline-flex -space-x-px'>
							<button
								type='button'
								className='px-3 py-1 border rounded-l disabled:opacity-50'
								onClick={() =>
									setSearchParams({
										page: String(page - 1),
										limit: String(limit),
									})
								}
								disabled={page <= 1}
							>
								Anterior
							</button>
							<span className='px-3 py-1 border-t border-b'>
								{page} / {filesMeta.lastPage}
							</span>
							<button
								type='button'
								className='px-3 py-1 border rounded-r disabled:opacity-50'
								onClick={() =>
									setSearchParams({
										page: String(page + 1),
										limit: String(limit),
									})
								}
								disabled={page >= filesMeta.lastPage}
							>
								Próxima
							</button>
						</nav>
					</div>
				)}
			</div>
		</Template>
	)
}
